"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "Sonic Concept",
  description: "We define the frequency landscape and emotional resonance of your project before the first note is even played.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/sonic_concept_phase.png"
}, {
  number: "02",
  title: "High-Fidelity Capture",
  description: "Using our world-class signal chain, we record every nuance of your performance with surgical precision and warmth.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/high_fidelity_capture.png"
}, {
  number: "03",
  title: "The Mixdown",
  description: "Our engineers blend digital clarity with analog soul, carving out space for every instrument to breathe and vibrate.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/mixdown_engineering.png"
}, {
  number: "04",
  title: "Final Mastering",
  description: "The final polish. We optimize your sound for every playback system, ensuring a powerful, professional sonic impact.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/final_mastering_polish.png"
}];
function StepCard({
  step,
  align
}: {
  step: typeof steps[0];
  align: 'left' | 'right';
}) {
  return <div className={`flex flex-col gap-6 w-full max-w-[400px] ${align === 'right' ? 'md:items-end md:text-right' : 'md:items-start md:text-left'} items-start text-left`}>
      <div className="relative overflow-hidden rounded-2xl border border-border/20 shadow-2xl group-hover:border-primary/50 transition-colors duration-500 bg-card/40 backdrop-blur-md">
        <img src={step.image} alt={step.title} className="w-[200px] h-[140px] object-cover transition-transform duration-700 group-hover:scale-110 opacity-80 group-hover:opacity-100" />
        <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent opacity-70 mix-blend-multiply pointer-events-none" />
      </div>
      <div className="flex flex-col gap-2">
        <div className="text-primary font-mono text-xs font-bold tracking-[0.3em] uppercase flex items-center gap-2">
          {align === 'right' && <span className="w-8 h-px bg-primary/30 hidden md:block" />}
          Phase {step.number}
          {align === 'left' && <span className="w-8 h-px bg-primary/30 hidden md:block" />}
        </div>
        <h3 className="font-default text-2xl md:text-3xl font-bold text-foreground tracking-tighter">{step.title}</h3>
        <p className="font-default text-muted-foreground text-sm md:text-base leading-relaxed max-w-[320px] font-medium">{step.description}</p>
      </div>
    </div>;
}
function StepItem({
  step,
  index
}: {
  step: typeof steps[0];
  index: number;
}) {
  const stepNumber = index + 1;
  const isEven = stepNumber % 2 === 0;
  const isOdd = !isEven;
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.6
  });
  return <div data-index={index} className="relative flex flex-col md:flex-row items-center justify-center w-full md:py-16 group">
      
      {}
      <div className="md:hidden absolute left-[23px] top-10 -translate-x-1/2 w-5 h-5 rounded-full bg-background border border-primary/40 z-10 flex items-center justify-center shadow-sm">
        <div className="w-2 h-2 rounded-full bg-primary shadow-[0_0_10px_var(--color-primary)]" />
      </div>

      {}
      <div className={`w-full md:w-1/2 flex ${isEven ? 'md:justify-end md:pr-20' : 'md:justify-end md:opacity-0 md:pointer-events-none hidden md:flex'}`}>
        {isEven && <StepCard step={step} align="right" />}
      </div>

      {}
      <div ref={ref} className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center z-10">
        <div className="w-10 h-10 rounded-full border border-primary/20 bg-background/80 flex items-center justify-center backdrop-blur-md">
          <div className={`w-3 h-3 rounded-full bg-primary transition-all duration-1000 ease-out ${isVisible ? "scale-100 opacity-100 shadow-[0_0_20px_var(--color-primary)]" : "scale-0 opacity-0"}`} />
        </div>
      </div>

      {}
      <div className={`w-full md:w-1/2 flex ${isOdd ? 'md:justify-start md:pl-20' : 'md:justify-start md:opacity-0 md:pointer-events-none hidden md:flex'}`}>
        {isOdd && <StepCard step={step} align="left" />}
      </div>

      {}
      <div className="md:hidden pl-14 w-full py-6">
        <StepCard step={step} align="left" />
      </div>
    </div>;
}
export default function TheDirectorsPath() {
  return <section data-section-id="223307" id="the-pulse-process" className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[140px] pointer-events-none -translate-x-1/2" />
      <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] pointer-events-none translate-x-1/2" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        
        <div className="text-center max-w-3xl mx-auto mb-20 md:mb-32">
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 tracking-tighter">
            The Pulse Process
          </h2>
          <p className="font-default text-muted-foreground text-lg md:text-xl leading-relaxed font-medium">
            A high-fidelity journey from initial vibration to a polished sonic masterpiece.
          </p>
        </div>

        <div className="relative max-w-6xl mx-auto">
          {}
          <div className="absolute left-1/2 top-4 bottom-4 w-px bg-gradient-to-b from-transparent via-primary/40 to-transparent -translate-x-1/2 hidden md:block" />
          
          {}
          <div className="absolute left-[23px] top-4 bottom-4 w-px bg-gradient-to-b from-transparent via-primary/40 to-transparent md:hidden" />

          <div className="flex flex-col gap-8 md:gap-0">
            {steps.map((step, i) => <StepItem key={i} step={step} index={i} />)}
          </div>
        </div>

      </div>
    </section>;
}